import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api';
import type { BackupStatusItem, BackupStatusKind } from './types';
import { SkeletonRows } from '../skeleton';

const KIND_ORDER: readonly BackupStatusKind[] = [
  'PHYSICAL_FULL',
  'PHYSICAL_INCR',
  'LOGICAL_DUMP',
  'RESTORE_DRILL',
];

function ResultBadge({ result }: { readonly result: BackupStatusItem['result'] }): React.JSX.Element {
  const { t } = useTranslation();
  const cls =
    result === 'SUCCESS' ? 'badge badge--success' : result === 'FAILURE' ? 'badge badge--danger' : 'badge';
  return <span className={cls}>{t(`backup.result${result}`)}</span>;
}

/**
 * 备份状态只读展示（M11，D-31 附录，backup.view）。
 * 只读——没有任何触发备份/恢复的按钮：那些是 pgBackRest cron 脚本与
 * docs/runbook-restore.md 里手工演练步骤的职责，不是这个页面的（Master Spec
 * §44 只要求"支持自动化备份"与"文档化恢复流程"，没有要求应用内可触发）。
 */
export function BackupStatusPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data, isPending } = useQuery({
    queryKey: ['backup-status'],
    queryFn: () => apiFetch<{ items: BackupStatusItem[] }>('/backup-status'),
  });

  const byKind = new Map((data?.items ?? []).map((i) => [i.kind, i]));

  return (
    <div>
      <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('backup.title')}</h1>
      <p className="field-hint">{t('backup.hint')}</p>

      {isPending ? (
        <SkeletonRows rows={4} />
      ) : (
        <div className="table-scroll" style={{ marginTop: 'var(--space-3)' }}>
          {/* 定宽 .data-cell：列永不被压皱错位；整块在 .table-scroll 里横滑
              （手机实测：普通 flex 子项被挤到换行，四列彻底错位） */}
          <div style={{ minWidth: '640px' }}>
            <div className="data-row data-row--head">
              <span className="data-cell" style={{ width: '200px' }}>{t('backup.colKind')}</span>
              <span className="data-cell" style={{ width: '110px' }}>{t('backup.colResult')}</span>
              <span className="data-cell" style={{ width: '180px' }}>{t('backup.colFinishedAt')}</span>
              <span className="data-cell" style={{ flex: 1 }}>{t('backup.colDetail')}</span>
            </div>
            {KIND_ORDER.map((kind) => {
              const item = byKind.get(kind);
              return (
                <div className="data-row" style={{ cursor: 'default' }} key={kind}>
                  <span className="data-cell" style={{ width: '200px' }} title={t(`backup.kind${kind}`)}>
                    {t(`backup.kind${kind}`)}
                  </span>
                  <span className="data-cell" style={{ width: '110px' }}>
                    <ResultBadge result={item?.result ?? 'NEVER_RUN'} />
                  </span>
                  <span className="data-cell" style={{ width: '180px' }}>
                    {item?.finishedAt ? fmt.format(new Date(item.finishedAt)) : '—'}
                  </span>
                  <span className="data-cell" style={{ flex: 1 }} title={item?.detail ?? ''}>
                    {item?.detail ?? '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
